import { useEffect } from 'react';
import { DropDownButton } from '@optimus-web/core';
import { LocalizedText, useAppClockService, useGainsightService, useScope } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { useCommanderSelector } from '@mews-commander/utils';
import { getActivePaymentCardIds, getChargeablePreauthorizationIds } from '../../../selectors';
import { isAnythingSelected } from '../../../ducks/billItems';
import { useInvalidatePreauthorizationTableRecordQuery } from '../../../../payment/queries';
import { PaymentCardOption } from './PaymentCardOption';
import { PreauthorizationOption } from './PreauthorizationOption';

interface ChargeAndCloseProps {
    accountId: string;
    billId: string;
}

const ChargeAndCloseButtonVisibleEventName = 'BillingScreenChargeAndCloseButtonVisible';

export const ChargeAndClose = ({ accountId, billId }: ChargeAndCloseProps) => {
    const scope = useScope();
    const appClockService = useAppClockService();
    const gainsightService = useGainsightService();
    const invalidatePreauthorizationTableRecordQuery = useInvalidatePreauthorizationTableRecordQuery();

    const paymentCardIds = useCommanderSelector((state) =>
        getActivePaymentCardIds(state, { accountId, appClockService })
    );
    const preauthorizationIds = useCommanderSelector((state) =>
        getChargeablePreauthorizationIds(state, { ownerId: accountId })
    );
    const disabled = useCommanderSelector((state) => isAnythingSelected(state, { scope }));

    useEffect(() => {
        gainsightService.trackEvent(ChargeAndCloseButtonVisibleEventName, { accountId, billId });
    }, [accountId, billId, gainsightService]);

    return (
        <DropDownButton
            label={<LocalizedText textKey={TextKey.ChargeAndClose} />}
            disabled={disabled}
            variant="primary"
            size="medium"
            wide
            data-test-charge-and-close-button
        >
            {preauthorizationIds.map((preauthorizationId) => (
                <PreauthorizationOption
                    accountId={accountId}
                    billId={billId}
                    key={preauthorizationId}
                    preauthorizationId={preauthorizationId}
                    invalidateQuery={() => invalidatePreauthorizationTableRecordQuery({ accountId })}
                />
            ))}
            {paymentCardIds.map((paymentCardId) => (
                <PaymentCardOption
                    billId={billId}
                    key={paymentCardId}
                    paymentCardId={paymentCardId}
                    disabled={disabled}
                />
            ))}
        </DropDownButton>
    );
};
